import type { S3File } from '../core/s3';

export type CardType = 'SINGLE_SELECT' | 'MULTI_SELECT' | 'FORM' | 'SPECIFIED_ITEMS' | 'TEXT' | 'COMPONENT';

export interface CardDefBase {
    id: string;
    title: string;
    titleInsured?: string;
    titleSystem?: string;
    type: CardType;
    logicalGroup1?: string;
    logicalGroup2?: string;
    logicalGroup3?: string;
    logicalGroup4?: string;
    logicalGroup5?: string;
    internalName?: string;
    nextCardId?: string;
    description?: string;
    isRoot?: boolean;
}

export interface CardDefSingleSelect extends CardDefBase {
    type: 'SINGLE_SELECT';
    required: boolean;
    children: CardChildDef[];
}

export interface CardDefMultiSelect extends CardDefBase {
    type: 'MULTI_SELECT';
    required: boolean;
    children: CardChildDef[];
}

export interface CardDefForm extends CardDefBase {
    type: 'FORM';
    children: CardChildDef[];
}

export interface CardDefSpecifiedItems extends CardDefBase {
    type: 'SPECIFIED_ITEMS';
    children: CardChildDef[];
}

export interface CardDefText extends CardDefBase {
    type: 'TEXT';
    content: string;
}

/** Registry keys of custom card renderers. Each key maps to a React component on the client. */
export const COMPONENT_CARD_KEYS = [
    'VEHICLE_DAMAGE_PICKER',
    'INCIDENT_LOCATION_PICKER',
    'NEARBY_REPAIRERS',
    'VEHICLE_INSPECTION',
    'CAPTURE_METHOD_SELECTOR',
] as const;
export type ComponentCardKey = (typeof COMPONENT_CARD_KEYS)[number];

export interface ComponentCardPanelDef {
    id: string;
    label: string;
}

/** A standalone photo the claimant must attach regardless of panel selection (e.g. odometer reading). */
export interface ComponentCardRequiredPhotoDef {
    id: string;
    label: string;
}

export interface VehicleDamageCardConfig {
    /** Minimum photos required per selected panel before the card validates. Defaults to 1; 0 = prompted only. */
    minPhotosPerPanel?: number;
    /** Overrides the default panel catalogue (labels and/or subset). */
    panels?: ComponentCardPanelDef[];
    /**
     * Standalone always-required photo uploads, independent of panel selection.
     * Defaults to odometer reading and VIN plate (VEHICLE_REQUIRED_PHOTOS); an
     * explicit empty array disables them.
     */
    requiredPhotos?: ComponentCardRequiredPhotoDef[];
}

export interface IncidentLocationCardConfig {
    /** Initial map centre before a position is captured. Signed decimal degrees. */
    defaultCenter?: { latitude: number; longitude: number };
    defaultZoom?: number;
}

export const NEARBY_REPAIRERS_DEFAULTS = {
    radiusKm: 50,
    maxResults: 10,
} as const;

/** Server clamps regardless of what a questionnaire author configures. */
export const NEARBY_REPAIRERS_LIMITS = {
    maxRadiusKm: 200,
    maxResults: 25,
} as const;

/**
 * Search parameters are read server-side from this config by the public
 * listNearbyRepairers endpoint — never from client input — so the org controls
 * its own exposure (docs/plan/supplier-geolocation/plan.md).
 */
export interface NearbyRepairersCardConfig {
    /** Search radius around the captured incident location. Default 50, clamped to 200. */
    radiusKm?: number;
    /** Maximum repairers listed. Default 10, clamped to 25. */
    maxResults?: number;
}

/** Inspection capture flow: Ravin's full walkaround or the simplified Lite capture (CUR-777). */
export type VehicleInspectionMode = 'FULL' | 'LITE';

export const VEHICLE_INSPECTION_DEFAULTS = {
    linkExpiryHours: 72,
    /**
     * Lite is opt-in per card (CUR-777): defaulting to FULL keeps existing
     * questionnaires on the walkaround they were configured with — a deploy
     * must not silently change live lodgement journeys.
     */
    inspectionMode: 'FULL' as VehicleInspectionMode,
    /** Lite links allow gallery uploads by default (`image-upload=true`); never applies to FULL. */
    allowImageUpload: true,
} as const;

/** Server clamps regardless of what a questionnaire author configures. */
export const VEHICLE_INSPECTION_LIMITS = {
    maxLinkExpiryHours: 168,
} as const;

/**
 * AI vehicle inspection (Ravin AI). The link-expiry window is read server-side
 * from this config by the public requestInspectionLink endpoint — never from
 * client input — so the org controls its own exposure
 * (docs/plan/ravin-ai-vehicle-inspection/plan.md).
 */
export interface VehicleInspectionCardConfig {
    /** Renders a "Skip this step" action. Default true. */
    allowSkip?: boolean;
    /** Shows optional registration/VIN inputs before the scan starts. Default true. */
    collectVehicleDetails?: boolean;
    /** Inspection link validity requested from the provider, in hours. Default 72, clamped to 168. */
    linkExpiryHours?: number;
    /** Capture flow: 'LITE' (simplified capture) or 'FULL' (walkaround). Default FULL (CUR-777). */
    inspectionMode?: VehicleInspectionMode;
    /** Allow gallery uploads in the Lite flow (`image-upload=true`). Default true; ignored for FULL. */
    allowImageUpload?: boolean;
}

/** How the claimant chose to capture the vehicle damage (CUR-621). */
export type CaptureMethod = 'AI_SCAN' | 'PHOTO_UPLOAD';

/**
 * Damage capture method selector (CUR-621): lets the claimant choose between
 * the AI walkaround scan and uploading photos themselves. The chosen method
 * routes the questionnaire — each option can continue to a different card,
 * falling back to the card's own nextCardId when a branch target is not set.
 */
export interface CaptureMethodSelectorCardConfig {
    /** Card to continue with when the claimant picks the AI scan. Falls back to the card's nextCardId. */
    scanNextCardId?: string;
    /** Card to continue with when the claimant picks photo upload. Falls back to the card's nextCardId. */
    uploadNextCardId?: string;
}

export type ComponentCardConfig =
    | VehicleDamageCardConfig
    | IncidentLocationCardConfig
    | NearbyRepairersCardConfig
    | VehicleInspectionCardConfig
    | CaptureMethodSelectorCardConfig;

interface CardDefComponentBase extends CardDefBase {
    type: 'COMPONENT';
    /**
     * Makes the component's own capture mandatory. For the vehicle damage picker
     * this requires at least one damaged panel — independent of
     * componentConfig.requiredPhotos, which already makes the step blocking on
     * its own. Set this only when panel damage itself is mandatory; claims with
     * no panel damage (e.g. mechanical failure) cannot pass a required card.
     */
    required?: boolean;
}

/** Keyed by `component` so each card's `componentConfig` is statically tied to its renderer. */
export type CardDefComponent =
    | (CardDefComponentBase & { component: 'VEHICLE_DAMAGE_PICKER'; componentConfig?: VehicleDamageCardConfig })
    | (CardDefComponentBase & { component: 'INCIDENT_LOCATION_PICKER'; componentConfig?: IncidentLocationCardConfig })
    | (CardDefComponentBase & { component: 'NEARBY_REPAIRERS'; componentConfig?: NearbyRepairersCardConfig })
    | (CardDefComponentBase & { component: 'VEHICLE_INSPECTION'; componentConfig?: VehicleInspectionCardConfig })
    | (CardDefComponentBase & {
          component: 'CAPTURE_METHOD_SELECTOR';
          componentConfig?: CaptureMethodSelectorCardConfig;
      });

export interface VehicleDamagePanelValue {
    panelId: string;
    label: string;
    files: S3File[];
}

export interface VehicleRequiredPhotoValue {
    photoId: string;
    label: string;
    files: S3File[];
}

export interface VehicleDamageCardValue {
    /** Selected panels only. */
    panels: VehicleDamagePanelValue[];
    /** Always-required standalone photos (e.g. odometer reading, VIN plate); entries created on first upload. */
    requiredPhotos?: VehicleRequiredPhotoValue[];
    /** Files marked isDeleted from deselected panels — kept so the server removes them from S3 at submission. */
    removedFiles?: S3File[];
    /** Set by the graceful client fallback (unknown component key) so validation never blocks. */
    skipped?: boolean;
}

/** Structured postal address — the value shape of the ADDRESS form control and the incident location picker's manual mode. */
export interface AddressValue {
    line1?: string;
    line2?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
}

export type IncidentLocationAddress = AddressValue;

/**
 * Discriminated on `skipped` so the graceful client fallback can write a value
 * without fabricating a `source` (the skipped branch carries no capture data).
 */
export type IncidentLocationCardValue =
    | { skipped: true }
    | {
          skipped?: false;
          /** The mode in effect at submission — validation and claim promotion key off this. */
          source: 'device' | 'manual';
          /** Signed decimal degrees. Negative latitude = southern hemisphere. */
          latitude?: number;
          longitude?: number;
          /** Metres, from the Geolocation API. */
          accuracy?: number;
          address?: IncidentLocationAddress;
          /** ISO 8601, when the device position was captured. */
          capturedAt?: string;
      };

/**
 * Display-first card: the claimant sees nearby repairers; selecting one is
 * optional and recorded for the claim handler. Supplier ids are stringified
 * bigints — card values must stay plain JSON.
 */
export interface NearbyRepairersCardValue {
    /** ISO 8601, set when results were shown to the claimant. */
    viewedAt?: string;
    selectedSupplierId?: string;
    selectedSupplierName?: string;
    /** Distance of the selected repairer from the incident, in km. */
    distanceKm?: number;
    /** Set when no device location was shared or by the graceful client fallback. */
    skipped?: boolean;
}

/**
 * Reference to an inspection minted server-side (VehicleInspection row); the
 * invitationId is the correlation key echoed back in the provider's report.
 * The value never carries provider credentials — only the one-time link the
 * claimant already holds.
 */
export interface VehicleInspectionCardValue {
    invitationId?: string;
    /** One-time inspection link, https-normalised server-side. */
    inspectionUrl?: string;
    /** ISO 8601, when the link was minted. */
    requestedAt?: string;
    /** ISO 8601, from the provider's expiration timestamp. */
    expiresAt?: string;
    registrationNumber?: string;
    vin?: string;
    /** Claimant chose to skip, or set by the graceful client fallback (unknown component key). */
    skipped?: boolean;
    /**
     * Whether the link currently held was minted with a completion redirect that
     * carries a server-side restore token. When false (e.g. the pre-mint checkpoint
     * failed, so no draft credentials reached the mint), a same-tab return cannot
     * reload the claim from the server — so the card must keep this tab alive rather
     * than navigate away. A routing signal, not a credential.
     */
    returnTokenIssued?: boolean;
    /**
     * Set once the provider's completion redirect has brought the claimant back. The
     * handoff token is single-use, so a spent link must not be re-launched same-tab
     * (it would return credential-free). Persisting this keeps the card on its
     * "uploaded" state — and off the reuse path — even after the `?scan=complete`
     * param is gone (e.g. resumed later from the emailed draft).
     */
    scanCompleted?: boolean;
}

export interface CaptureMethodSelectorCardValue {
    /** The capture method the claimant picked. */
    selected?: CaptureMethod;
    /** Set by the graceful client fallback (unknown component key on an older bundle). */
    skipped?: boolean;
}

export type ComponentCardValue =
    | VehicleDamageCardValue
    | IncidentLocationCardValue
    | NearbyRepairersCardValue
    | VehicleInspectionCardValue
    | CaptureMethodSelectorCardValue;

/** A location value that actually captured something (the skipped marker carries no data). */
export type CapturedIncidentLocation = Exclude<IncidentLocationCardValue, { skipped: true }>;

/**
 * Stored component-card values carry no discriminant of their own (pre-existing vehicle
 * damage values predate the union), so narrowing always keys off the card's `component`.
 */
export function isVehicleDamageValue(
    component: ComponentCardKey,
    value: ComponentCardValue | null | undefined,
): value is VehicleDamageCardValue | null | undefined {
    return component === 'VEHICLE_DAMAGE_PICKER';
}

export function isIncidentLocationValue(
    component: ComponentCardKey,
    value: ComponentCardValue | null | undefined,
): value is IncidentLocationCardValue | null | undefined {
    return component === 'INCIDENT_LOCATION_PICKER';
}

export function isNearbyRepairersValue(
    component: ComponentCardKey,
    value: ComponentCardValue | null | undefined,
): value is NearbyRepairersCardValue | null | undefined {
    return component === 'NEARBY_REPAIRERS';
}

export function isVehicleInspectionValue(
    component: ComponentCardKey,
    value: ComponentCardValue | null | undefined,
): value is VehicleInspectionCardValue | null | undefined {
    return component === 'VEHICLE_INSPECTION';
}

export function isCaptureMethodSelectorValue(
    component: ComponentCardKey,
    value: ComponentCardValue | null | undefined,
): value is CaptureMethodSelectorCardValue | null | undefined {
    return component === 'CAPTURE_METHOD_SELECTOR';
}

export function isValidLatLng(latitude: unknown, longitude: unknown): boolean {
    return (
        typeof latitude === 'number' &&
        Number.isFinite(latitude) &&
        latitude >= -90 &&
        latitude <= 90 &&
        typeof longitude === 'number' &&
        Number.isFinite(longitude) &&
        longitude >= -180 &&
        longitude <= 180
    );
}

/**
 * One-line display form of a structured address. Values arrive from untyped
 * claimant-controlled formData JSON — non-string parts are dropped rather than
 * trusted, so a crafted submission cannot crash PDF generation or AI extraction.
 */
export function formatAddressValue(address: AddressValue | undefined): string {
    if (!address) {
        return '';
    }
    return [address.line1, address.line2, address.suburb, address.state, address.postcode]
        .map((part) => (typeof part === 'string' ? part.trim() : ''))
        .filter(Boolean)
        .join(', ');
}

/** One-line display form of a manually entered incident address. */
export const formatIncidentLocationAddress = formatAddressValue;

/** Display form of captured coordinates, e.g. `-33.86880, 151.20930 (±12 m)`. */
export function formatLatLng(latitude: number, longitude: number, accuracy?: number): string {
    const base = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
    return accuracy ? `${base} (±${Math.round(accuracy)} m)` : base;
}

export type CardDef =
    | CardDefSingleSelect
    | CardDefMultiSelect
    | CardDefForm
    | CardDefSpecifiedItems
    | CardDefText
    | CardDefComponent;

export type CardChildInputType =
    | 'TEXT'
    | 'DATE'
    | 'NUMBER'
    | 'TIME'
    | 'CURRENCY'
    | 'UPLOAD'
    | 'SELECT'
    | 'YES_NO'
    | 'DATETIME'
    | 'SPECIFIED_ITEM'
    | 'EMAIL'
    | 'OPTION'
    | 'PHONE_NUMBER'
    | 'IMAGE_MAP'
    | 'TEXTAREA'
    | 'ADDRESS';

export interface CardChildDefBase {
    id: string;
    title: string;
    type: CardChildInputType;
    parentCardId?: string;
    order?: number;
    nextCardId?: string;
    additionalCardId?: string;
    internalName?: string;
    validationSchema?: string;
    componentConfig?: any;
    description?: string;
    required?: boolean;
    toJS: () => any;
}

export interface CardChildDefText extends CardChildDefBase {
    type: 'TEXT';
}

export interface CardChildDefDate extends CardChildDefBase {
    type: 'DATE';
}

export interface CardChildDefNumber extends CardChildDefBase {
    type: 'NUMBER';
}

export interface CardChildDefTime extends CardChildDefBase {
    type: 'TIME';
}

export interface CardChildDefCurrency extends CardChildDefBase {
    type: 'CURRENCY';
}

export interface CardChildDefEmail extends CardChildDefBase {
    type: 'EMAIL';
}

export interface CardChildDefPhoneNumber extends CardChildDefBase {
    type: 'PHONE_NUMBER';
}

export interface CardChildDefUpload extends CardChildDefBase {
    type: 'UPLOAD';
    fileCategories: string[];
    allowedFileTypes: string[];
}

export interface CardChildDefSelect extends CardChildDefBase {
    type: 'SELECT';
    options: Array<{ label: string; value: string }>;
}

export interface CardChildDefYesNo extends CardChildDefBase {
    type: 'YES_NO';
}

export interface CardChildDefSpecifiedItem extends CardChildDefBase {
    type: 'SPECIFIED_ITEM';
    specifiedItemId: number | null;
    pdsConfigItemId: number | null;
}

export interface CardChildDefOption extends CardChildDefBase {
    type: 'OPTION';
    showAsOther: boolean;
}

export interface CardChildDefDateTime extends CardChildDefBase {
    type: 'DATETIME';
}

export interface CardChildDefImageMap extends CardChildDefBase {
    type: 'IMAGE_MAP';
    imageMap: string;
    imageUrl: string;
}

export interface CardChildDefTextArea extends CardChildDefBase {
    type: 'TEXTAREA';
}

export interface CardChildDefAddress extends CardChildDefBase {
    type: 'ADDRESS';
}

export type CardChildDef =
    | CardChildDefText
    | CardChildDefDate
    | CardChildDefNumber
    | CardChildDefTime
    | CardChildDefCurrency
    | CardChildDefUpload
    | CardChildDefSelect
    | CardChildDefYesNo
    | CardChildDefSpecifiedItem
    | CardChildDefOption
    | CardChildDefDateTime
    | CardChildDefEmail
    | CardChildDefPhoneNumber
    | CardChildDefImageMap
    | CardChildDefTextArea
    | CardChildDefAddress;

export type InputTypeToValueType<T extends CardChildInputType> = T extends 'TEXT'
    ? string
    : T extends 'DATE'
      ? string
      : T extends 'NUMBER'
        ? number | null
        : T extends 'TIME'
          ? string
          : T extends 'CURRENCY'
            ? number | null
            : T extends 'UPLOAD'
              ? string
              : T extends 'SELECT'
                ? string
                : T extends 'YES_NO'
                  ? boolean | null
                  : T extends 'DATETIME'
                    ? string
                    : T extends 'SPECIFIED_ITEM'
                      ? number | undefined
                      : T extends 'EMAIL'
                        ? string
                        : T extends 'PHONE_NUMBER'
                          ? string
                          : T extends 'OPTION'
                            ? string
                            : T extends 'IMAGE_MAP'
                              ? string
                              : T extends 'TEXTAREA'
                                ? string
                                : T extends 'ADDRESS'
                                  ? AddressValue | null
                                  : never;

export type FormValues = Record<string, any>;

// Do not remove this type, it is used for JSON schema validation
export type QuestionnaireDef = CardDef[];
